import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  BookOpen, Printer, Search, ChevronRight, ShieldCheck, Users, Boxes,
  ShoppingCart, ShoppingBag, ScanBarcode, Truck, Undo2, Wallet, HandCoins,
  FileText, Receipt, Landmark, Banknote, FileClock, BarChart3, Package,
  Settings as SettingsIcon, MessageSquare, KeyRound, ClipboardList
} from "lucide-react";

const SECTIONS = [
  {
    id: "intro",
    title: "Getting Started",
    icon: BookOpen,
    body: (
      <>
        <p>
          Welcome to <b>AccountifyPro</b> — a multi-tenant accounting platform built for
          Indian MSMEs. This manual walks you through every screen, portal and workflow
          in the order you will typically use them. Use the search bar or the left menu
          to jump to any section.
        </p>
        <ul>
          <li><b>Super Admin</b> approves new firms and manages platform-level access.</li>
          <li><b>Owner portal</b> is for the firm proprietor — view all data, reports, settings.</li>
          <li><b>Accountant portal</b> is for day-to-day data entry and billing.</li>
        </ul>
        <p className="tip">
          Helpline: <b>8446463026</b> • Developed by <b>ANV ASSOCIATE</b>.
        </p>
      </>
    ),
  },
  {
    id: "signup-login",
    title: "Firm Sign-up & Login",
    icon: ShieldCheck,
    body: (
      <>
        <ol>
          <li>Open the landing page and click <b>Register your Firm</b>.</li>
          <li>Fill firm details — <i>firm name, GSTIN, address, owner name, mobile</i>, and set both an <b>Owner PIN</b> and a <b>Firm Edit PIN</b>.</li>
          <li>Submit — the request goes to the <b>Super Admin</b> for approval.</li>
          <li>Once approved, use <b>Firm Login</b> with the credentials shown after signup.</li>
          <li>After login you land on <b>Portal Select</b> — choose <b>Owner</b> or <b>Accountant</b>.</li>
        </ol>
        <p className="tip">
          Forgot password? Click <b>Forgot Password</b> on the login screen. A request is
          pushed to Super Admin who can view/reset your credentials from their dashboard.
        </p>
      </>
    ),
  },
  {
    id: "super-admin",
    title: "Super Admin Console",
    icon: KeyRound,
    body: (
      <>
        <p>Accessible only to the platform administrator at <code>/super-admin</code>.</p>
        <ul>
          <li><b>Firm Requests</b> — approve or reject pending firms.</li>
          <li><b>Approved Firms</b> — view credentials (Owner ID/PW, Accountant ID/PW), toggle status, reset PINs.</li>
          <li><b>Password Requests</b> — see forgot-password requests raised by any firm and share fresh credentials over WhatsApp/phone.</li>
        </ul>
      </>
    ),
  },
  {
    id: "dashboard",
    title: "Dashboard",
    icon: BarChart3,
    body: (
      <>
        <p>Your daily nerve-centre. It shows:</p>
        <ul>
          <li><b>Receivables</b> — total money to be received from parties.</li>
          <li><b>Payables</b> — total money you owe.</li>
          <li><b>Sales & Purchases</b> — MTD/FYD totals.</li>
          <li><b>Low-stock alerts</b> — items below minimum stock.</li>
          <li><b>Cheque reminders</b> — auto-scanned every visit, pushed to the bell 🔔.</li>
        </ul>
      </>
    ),
  },
  {
    id: "parties",
    title: "Parties (Customers & Suppliers)",
    icon: Users,
    body: (
      <>
        <ol>
          <li>Open <b>Parties</b> from the sidebar.</li>
          <li>Click <b>+ Add Party</b> — enter name, phone, GSTIN, address, opening balance and type (customer / supplier / both).</li>
          <li>Click any row to open <b>Party Detail</b> — full ledger, running balance, quick <b>WhatsApp Reminder</b> and <b>Statement PDF</b>.</li>
          <li>Use <b>WhatsApp</b> icon to draft a free click-to-chat reminder using the party's phone and pending amount.</li>
        </ol>
      </>
    ),
  },
  {
    id: "items",
    title: "Items / Stock",
    icon: Boxes,
    body: (
      <>
        <ol>
          <li>Go to <b>Items / Stock</b> and click <b>+ Add Item</b>.</li>
          <li>Fill name, HSN, unit, GST rate, sale/purchase price, opening stock and <b>minimum stock</b>.</li>
          <li>Upload an item image if needed (uses Emergent Object Storage).</li>
          <li>Barcode scanning is supported in POS — just print/paste the item's barcode on the product.</li>
        </ol>
        <p className="tip">Items below <b>min stock</b> appear in Dashboard's low-stock alerts.</p>
      </>
    ),
  },
  {
    id: "sales",
    title: "Sales / Invoicing",
    icon: ShoppingCart,
    body: (
      <>
        <ol>
          <li>Open <b>Sales</b> → click <b>New Sale</b>.</li>
          <li>Pick the party (or add on-the-fly). Add items — GST is auto-computed based on HSN/rate.</li>
          <li>Optional: enter <b>Vehicle No.</b>, <b>Transporter</b> and <b>Freight/Delivery Charges</b>.</li>
          <li>Choose payment mode (Cash / Bank / UPI / Cheque / Credit).</li>
          <li>Save — the invoice appears in the list with actions: <b>Print</b>, <b>Share on WhatsApp</b>, <b>Convert to Delivery Challan</b>.</li>
        </ol>
        <p className="tip">
          Invoice print shows firm logo, signature, <b>UPI QR</b>, HSN summary and
          e-invoicing/e-way-bill placeholders.
        </p>
      </>
    ),
  },
  {
    id: "estimates-proforma",
    title: "Estimates, Quotations & Proforma",
    icon: FileText,
    body: (
      <>
        <p>
          Use <b>Quotations / Estimates</b> for pre-sale offers and <b>Proforma</b> for
          advance-payment invoices. Both can be one-click <b>converted to a Sale</b>
          once confirmed — item quantities and totals carry over.
        </p>
      </>
    ),
  },
  {
    id: "delivery-challan",
    title: "Delivery Challan",
    icon: Truck,
    body: (
      <>
        <ol>
          <li>Open <b>Delivery Challan</b>.</li>
          <li>Pick a party and items <em>or</em> convert directly from an existing sale.</li>
          <li>Enter <b>vehicle number</b>, <b>driver</b>, <b>transporter</b> and <b>freight charges</b>.</li>
          <li>Print the challan or share via WhatsApp.</li>
        </ol>
      </>
    ),
  },
  {
    id: "pos",
    title: "POS (Point of Sale)",
    icon: ScanBarcode,
    body: (
      <>
        <ol>
          <li>Open <b>POS</b>. Scan a barcode or search items.</li>
          <li>Add customer phone to auto-attach <b>loyalty points</b>.</li>
          <li>Accept payment (Cash / UPI / Card) and print thermal receipt.</li>
        </ol>
        <p className="tip">POS bills appear in Sales list marked as <b>POS</b>.</p>
      </>
    ),
  },
  {
    id: "purchases",
    title: "Purchase Bills & Expenses",
    icon: ShoppingBag,
    body: (
      <>
        <p>
          Use <b>Purchase Bills</b> for supplier invoices and <b>Expenses</b> for
          non-inventory costs (rent, salary, utilities). Both support attachments —
          upload the scanned bill via the paperclip icon.
        </p>
      </>
    ),
  },
  {
    id: "returns",
    title: "Sales Return / Purchase Return",
    icon: Undo2,
    body: (
      <>
        <p>
          Raise a <b>Credit Note</b> (Sales Return) from the Sales module or a
          <b> Debit Note</b> (Purchase Return) from Purchases. Stock is auto-adjusted and
          party balance is updated.
        </p>
      </>
    ),
  },
  {
    id: "payments",
    title: "Payment In / Payment Out",
    icon: HandCoins,
    body: (
      <>
        <ol>
          <li>Open <b>Payment In</b> to record money received (or <b>Payment Out</b> for money paid).</li>
          <li>Select party → pick invoices to settle (or leave as on-account).</li>
          <li>Choose mode: Cash / Bank / UPI / Cheque. For cheque, enter number & date — it goes to <b>Cheques</b> tracker.</li>
        </ol>
      </>
    ),
  },
  {
    id: "pending",
    title: "Pending Orders & Pending Purchase",
    icon: ClipboardList,
    body: (
      <>
        <p>
          Orders that are booked but not yet dispatched sit in <b>Pending Orders</b>;
          purchase orders raised but not received sit in <b>Pending Purchase</b>. Convert
          to full invoice/purchase from these screens with one click.
        </p>
      </>
    ),
  },
  {
    id: "bank-cash-cheques",
    title: "Bank, Cash & Cheques",
    icon: Landmark,
    body: (
      <>
        <ul>
          <li><b>Bank</b> — add bank accounts, view statements, reconcile.</li>
          <li><b>Cash in Hand</b> — track cash inflow/outflow with daily closing balance.</li>
          <li><b>Cheques</b> — status: pending / cleared / bounced. Auto reminders on due-date.</li>
        </ul>
      </>
    ),
  },
  {
    id: "loans",
    title: "Loan Accounts & EMI",
    icon: HandCoins,
    body: (
      <>
        <p>
          Track business loans — principal, interest rate, tenure, monthly EMI and
          outstanding balance. Each EMI paid is auto-split into principal + interest and
          reflected in Balance Sheet.
        </p>
      </>
    ),
  },
  {
    id: "reports",
    title: "Reports & GST Filings",
    icon: BarChart3,
    body: (
      <>
        <ul>
          <li><b>GSTR-1</b> — outward supplies with auto-populated buyer GSTINs.</li>
          <li><b>GSTR-3B</b> — summary of outward + inward tax.</li>
          <li><b>Balance Sheet</b>, <b>Trial Balance</b>, <b>P&L</b>.</li>
          <li><b>Party Statement</b>, <b>Item Report</b>, <b>Day Book</b>.</li>
        </ul>
        <p className="tip">Every report supports <b>PDF</b> and <b>Excel</b> export.</p>
      </>
    ),
  },
  {
    id: "bulk-export",
    title: "Bulk PDF Export",
    icon: Package,
    body: (
      <>
        <p>
          Select a date range and transaction type — the app zips all matching invoices
          into a single downloadable archive. Ideal for auditor handover at year-end.
        </p>
      </>
    ),
  },
  {
    id: "whatsapp",
    title: "WhatsApp Integration (Free)",
    icon: MessageSquare,
    body: (
      <>
        <p>
          Every party / invoice screen has a WhatsApp icon. It uses free
          <code> wa.me </code> click-to-chat links — no Twilio, no subscription. The
          message is pre-filled with:
        </p>
        <ul>
          <li>Firm name, phone and GSTIN</li>
          <li>Invoice number, date and amount</li>
          <li>Pending amount reminder text (if applicable)</li>
        </ul>
      </>
    ),
  },
  {
    id: "settings",
    title: "Settings",
    icon: SettingsIcon,
    body: (
      <>
        <p>Owner-only. Configure:</p>
        <ul>
          <li>Firm logo & owner signature (uploaded to Object Storage).</li>
          <li>Change <b>Firm Edit PIN</b> and <b>Owner PIN</b>.</li>
          <li>Invoice number series and financial year.</li>
          <li>UPI ID for QR generation on invoices.</li>
        </ul>
      </>
    ),
  },
  {
    id: "pin",
    title: "Edit PIN & Security",
    icon: ShieldCheck,
    body: (
      <>
        <p>
          Editing or deleting any transaction prompts a <b>Firm Edit PIN</b>. Only the
          Owner can change this PIN from Settings. This protects historical data from
          accidental accountant edits.
        </p>
      </>
    ),
  },
  {
    id: "faq",
    title: "FAQ / Troubleshooting",
    icon: BookOpen,
    body: (
      <>
        <ul>
          <li><b>Screen blank after login?</b> Log out, clear browser cache, re-login. Contact helpline if it persists.</li>
          <li><b>Can I use the app offline?</b> No, the app requires internet.</li>
          <li><b>How to backup my data?</b> Use <b>Bulk PDF Export</b> plus <b>Reports → Excel export</b> monthly.</li>
          <li><b>Password lost?</b> Use <b>Forgot Password</b>; Super Admin will share fresh credentials.</li>
        </ul>
      </>
    ),
  },
];

export default function UserManual() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState("intro");

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return SECTIONS;
    return SECTIONS.filter((s) => {
      const text = (s.title + " " + JSON.stringify(s.body?.props || {})).toLowerCase();
      return text.includes(t);
    });
  }, [q]);

  const handlePrint = () => window.print();

  return (
    <div className="max-w-6xl mx-auto" data-testid="user-manual-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-6 no-print">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-[#064E3B] text-white flex items-center justify-center">
            <BookOpen size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="manual-title">User Manual</h1>
            <p className="text-xs text-muted-foreground">AccountifyPro — complete usage guide</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search manual…"
              className="pl-8 w-64"
              data-testid="manual-search"
            />
          </div>
          <Button onClick={handlePrint} variant="outline" data-testid="manual-print-btn">
            <Printer size={14} className="mr-2" /> Print / Save PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* TOC */}
        <aside className="col-span-12 md:col-span-4 lg:col-span-3 no-print">
          <div className="sticky top-4 bg-white border border-border rounded-md p-3">
            <div className="text-[10px] overline mb-2 text-slate-500">Contents</div>
            <nav className="space-y-1 max-h-[70vh] overflow-y-auto">
              {filtered.map((s) => {
                const Icon = s.icon;
                const isActive = active === s.id;
                return (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={() => setActive(s.id)}
                    data-testid={`manual-toc-${s.id}`}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-slate-50 ${
                      isActive ? "bg-[#ECFDF5] text-[#064E3B] font-semibold" : "text-slate-700"
                    }`}
                  >
                    <Icon size={14} />
                    <span className="flex-1 truncate">{s.title}</span>
                    <ChevronRight size={12} className="opacity-50" />
                  </a>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article
          className="col-span-12 md:col-span-8 lg:col-span-9 bg-white border border-border rounded-md p-6 space-y-10 manual-content print-area"
          data-testid="manual-content"
        >
          <div className="print-only text-center mb-6">
            <h1 className="text-3xl font-bold text-[#064E3B]">AccountifyPro</h1>
            <p className="text-sm text-slate-600">User Manual</p>
            <hr className="my-4" />
          </div>
          {filtered.length === 0 && (
            <div className="text-sm text-muted-foreground">No sections match “{q}”.</div>
          )}
          {filtered.map((s) => {
            const Icon = s.icon;
            return (
              <section id={s.id} key={s.id} data-testid={`manual-section-${s.id}`} className="scroll-mt-24">
                <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-3">
                  <Icon size={20} className="text-[#064E3B]" />
                  {s.title}
                </h2>
                <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed">
                  {s.body}
                </div>
              </section>
            );
          })}
          <div className="pt-6 border-t border-border text-xs text-muted-foreground">
            © {new Date().getFullYear()} AccountifyPro • Developed by ANV ASSOCIATE • Helpline 8446463026
          </div>
        </article>
      </div>

      {/* Manual styles */}
      <style>{`
        .manual-content ol { list-style: decimal; padding-left: 1.25rem; margin: 0.5rem 0; }
        .manual-content ul { list-style: disc; padding-left: 1.25rem; margin: 0.5rem 0; }
        .manual-content li { margin: 0.25rem 0; }
        .manual-content p { margin: 0.5rem 0; }
        .manual-content code {
          background: #F1F5F9; padding: 1px 6px; border-radius: 4px; font-size: 12px;
        }
        .manual-content .tip {
          background: #ECFDF5; border-left: 3px solid #064E3B;
          padding: 8px 12px; margin: 8px 0; border-radius: 4px; font-size: 13px;
        }
        .print-only { display: none; }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .manual-content { border: none !important; padding: 0 !important; }
          .manual-content section { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
